/**
 * `localStorage` that cannot take the page down.
 *
 * Safari on iOS throws `SecurityError: The operation is insecure.` on *any*
 * touch of `window.localStorage` when the visitor has Settings → Safari →
 * "Block All Cookies" turned on; managed and parental-control profiles do the
 * same. Private mode adds `QuotaExceededError` on writes.
 *
 * The throw lands on the property access itself, so a bare
 * `localStorage.getItem(...)` evaluated at module scope aborts the whole bundle
 * before React ever mounts. The symptom is a blank white page with no UI at
 * all — which is exactly what iOS visitors were getting on the landing page.
 *
 * Reads degrade to `null` here and writes to a no-op. Nothing we keep in
 * storage is load-bearing: a missing token just means the visitor stays
 * anonymous and sees the landing page, and everything else is UI preference.
 */

export function getItem(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** @returns {boolean} whether the value actually got stored. */
export function setItem(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** @returns {boolean} whether the key actually got removed. */
export function removeItem(key) {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * True when storage is readable and writable. Use only to decide whether to
 * offer a feature that is pointless without persistence — the three accessors
 * above are already safe on their own and need no guarding.
 */
export function isAvailable() {
  const probe = "__qoldau_storage_probe__";
  try {
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/** Remove every key starting with `prefix`. Best effort, like the rest.
 *  @returns {number} how many were removed. */
export function removeByPrefix(prefix) {
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
    return keys.length;
  } catch {
    return 0;
  }
}

// The same three accessors over sessionStorage, for a value that belongs to
// one tab and one sign-in attempt. Same failure mode on iOS, same degrade.
export function getSessionItem(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setSessionItem(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeSessionItem(key) {
  try {
    window.sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

const safeStorage = {
  getItem, setItem, removeItem, removeByPrefix, isAvailable,
  getSessionItem, setSessionItem, removeSessionItem,
};

export default safeStorage;
